import { TextButton } from 'components/common';
import {
  getDefaultSelect,
  mapSelect,
  TStateValue,
} from 'components/core/FilterSelects/utils';
import { useContext, useEffect, useMemo, useState } from 'react';
import { FilterContext } from 'utils/context';
import { TFilterOptions } from 'utils/context/FilterContext/types';

import { Box, Flex, Icon, Typography } from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

import { FilterSelectRow } from './components';

interface Props {
  options: TFilterOptions;
  title: string;
  startVariant?: string;
}

function FilterSelects({ options, startVariant, title }: Props) {
  const { t } = useTranslation();
  const { cache, filters, setFiltersKey } = useContext(FilterContext);
  const filter = useMemo(
    () => (filters[title] || [['', '', ['']]]) as TStateValue[],
    [filters, title],
  );
  const [showUndo, setShowUndo] = useState(false);
  const [selections, setSelections] = useState(filter.map(mapSelect));

  useEffect(() => {
    setSelections(filter.map(mapSelect));
  }, [filter]);

  const rows = useMemo(() => {
    const handleClear = (index: number) => {
      const selects = selections.filter((_, idx) => idx !== index);
      const next = selects.length ? selects : [getDefaultSelect()];
      const nextFilter = next.map(({ value }) => value);
      setSelections(next);
      setFiltersKey(title, nextFilter);
      setShowUndo(true);
    };

    const handleSelect = (
      value: string | string[],
      index: number,
      idx: number,
    ) => {
      const selects = selections.length
        ? [...selections]
        : [getDefaultSelect()];
      selects[index].value[idx] = value;
      if (idx < 1) selects[index].value[1] = '';
      if (idx < 2) selects[index].value[2] = [''];
      const next = selects.map(({ value }) => value);
      setShowUndo(false);
      setSelections(selects);
      setFiltersKey(title, next);
    };

    const criteriaOptions = options
      .map(({ option }) => option[0])
      .filter((opt) => !selections.some(({ value }) => value[0] === opt));

    return selections.map((select, index) => {
      return (
        <FilterSelectRow
          criteriaOptions={criteriaOptions}
          handleClear={handleClear}
          handleSelect={handleSelect}
          index={index}
          key={select.value[0] || select.id}
          options={options}
          startVariant={startVariant}
          values={select.value}
        />
      );
    });
  }, [options, selections, setFiltersKey, startVariant, title]);

  return (
    <Box data-testid={title}>
      {rows}
      <Flex align="center" gap="x-small">
        {(selections.length !== options.length || startVariant) && (
          <TextButton
            isDisabled={selections?.some((select) => !select.value[0])}
            startIcon={<Icon icon="add" />}
            text={t('core.addCriteria')}
            onPress={() => {
              setSelections((selections || []).concat([getDefaultSelect()]));
              setShowUndo(false);
            }}
          />
        )}
        {showUndo && (
          <>
            <Typography css={{ color: '#324DA9' }} children="|" />
            <TextButton
              text="Undo"
              onPress={() => {
                const fallback = [['', '', ['']]];
                const prev = (cache[title] || fallback) as Array<TStateValue>;
                setSelections(prev.map(mapSelect));
                setFiltersKey(title, prev);
                setShowUndo(false);
              }}
            />
          </>
        )}
      </Flex>
    </Box>
  );
}

export default FilterSelects;
